/**
 * @file NoteMeetingReports.tsx
 * @brief "My notes x this meeting" on the web: each line of a note, what the
 *        meeting said about it, and the moment itself (play to hear it).
 *
 * @details
 * Web port of the phone's NoteMeetingReports (29-09-2026), built for the
 * Meeting Notes view of a meeting page (ADR-030, 06-10-2026). What it shows
 * for one note, visible to its author only:
 *   - every line: Discussed / Partly discussed / Not discussed, what was said
 *     about it (secondary text: the owner's own words lead, the model's
 *     additions sit below them), the transcript words as a short quote, and
 *     a play button that plays that moment of the recording;
 *   - a question line: answered, partly or not, and the answer;
 *   - the meeting's to-dos that fall to the author.
 *
 * The server's evidence rule applies before anything reaches here: a claim
 * without words really in the transcript comes back as "not discussed".
 *
 * A report is built the first time this view sees a finished linked meeting
 * without one (one text call on the server, no audio), one at a time, and is
 * reused after that. When the note or the meeting changed, it says so and
 * offers "Update" rather than spending again on its own. A failed build is
 * not retried in a loop: the next try is the user's "Try again".
 *
 * `meetingId` limits the view to that one meeting (the meeting page); without
 * it every linked meeting is listed, as the phone's Note detail does.
 * The whole thing hides itself when the note has no lines, has no finished
 * meeting, or the server does not offer reports.
 *
 * @dependencies
 * - ./api: useNoteMeetingReports, useGenerateNoteMeetingReport
 * - components/ui/AudioPlayer: AudioPlayBadge (the clip player)
 * - company-meetings/components/meetingEvidence: readStoredAnchor, anchorLabel
 *   (so a moment sounds and reads the same as on the rest of the meeting page)
 *
 * @references
 * - FRONTEND/src/screens/Notes/NoteMeetingReports.tsx (the phone original)
 * - BACKEND/src/features/notes/noteMeetingReport.service.mjs
 * - docs/decisions/ADR-030-meeting-notes-tier.md
 *
 * @author DES
 * @date 06-10-2026
 */

import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  Circle,
  CircleCheck,
  CircleDashed,
  LoaderCircle,
  Lock,
  RefreshCw,
  PenLine,
} from "lucide-react";
import { TodoTick } from "@/components/icons/TodoTick";
import { BoardroomTable } from "@/components/icons/BoardroomTable";
import { Link } from "react-router-dom";
import { AudioPlayBadge } from "@/components/ui/AudioPlayer";
import { ApiClientError } from "@/shared/api/client";
import { cn } from "@/shared/theme/cn";
import { isRTLText } from "@/shared/utils/formatters";
import {
  anchorClipSecs,
  anchorLabel,
  readStoredAnchor,
} from "@/features/company-meetings/components/meetingEvidence";
import { useGenerateNoteMeetingReport, useNoteMeetingReports } from "./api";
import { NOTHING_SAID_MESSAGE, noteReportErrorText } from "./noteFormat";
import type { NoteMeetingReportEntry, NoteReportAnchor, NoteReportPoint } from "./schemas";

// ----------------------------------------------------------------------
// Copy + small helpers (the phone's, verbatim)
// ----------------------------------------------------------------------
const STATUS_LABEL: Record<NoteReportPoint["status"], string> = {
  discussed: "Discussed",
  partly: "Partly discussed",
  not_discussed: "Not discussed",
};

const ANSWERED_LABEL: Record<"yes" | "partly" | "no", string> = {
  yes: "Answered",
  partly: "Partly answered",
  no: "Not answered",
};

/**
 * The resolver's quote is the whole matched span, which on a long Urdu turn
 * ran to eight lines. A cue to the moment, not a transcript: the words up to
 * ~140 characters, cut at a word. (Phone rule, 29-09-2026.)
 */
const QUOTE_MAX = 140;
function shortQuote(q: string): string {
  const t = q.replace(/\s+/g, " ").trim();
  if (t.length <= QUOTE_MAX) return t;
  const cut = t.slice(0, QUOTE_MAX);
  const at = cut.lastIndexOf(" ");
  return `${(at > 80 ? cut.slice(0, at) : cut).trim()}…`;
}

function StatusIcon({ status }: { status: NoteReportPoint["status"] }) {
  if (status === "discussed") return <CircleCheck className="h-5 w-5 text-brand" />;
  if (status === "partly") return <CircleDashed className="h-5 w-5 text-brand" />;
  return <Circle className="h-5 w-5 text-app-text-secondary/60" />;
}

/** Play this moment: the meeting page's clip rules (readStoredAnchor). */
function MomentButton({
  url,
  anchor,
}: {
  url: string | null | undefined;
  anchor: NoteReportAnchor | null | undefined;
}) {
  // A report anchor may carry no end; the phone then plays ~12s from the
  // start, and so does this (start + 10s, plus the clip's lead-in and tail).
  const start = typeof anchor?.startSecs === "number" ? anchor.startSecs : null;
  const end =
    typeof anchor?.endSecs === "number" && start !== null && anchor.endSecs > start
      ? anchor.endSecs
      : start !== null
        ? start + 10
        : null;
  const voice = anchor ? readStoredAnchor({ ...anchor, endSecs: end }) : null;
  if (!url || !voice) return null;
  return (
    <AudioPlayBadge
      src={url}
      variant="original"
      startSecs={voice.startSecs}
      clipSecs={anchorClipSecs(voice)}
      label={`Hear it · ${anchorLabel(voice)}`}
      title={`Play this moment (${anchorLabel(voice)})`}
      className="mt-0.5 h-7 self-start normal-case tracking-normal"
    />
  );
}

// ----------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------
type Props = {
  noteId: string;
  /** Only this meeting's report (the meeting page). */
  meetingId?: string;
  /** Heading for the meeting page: the note's title instead of "From the
   *  meeting" + the meeting's title. */
  noteTitle?: string;
  /** Played when the report carries no signed URL of its own. */
  fallbackRecordingUrl?: string | null;
  /** Changes when the note's lines change, to reload (the server marks the
   *  report stale). */
  noteVersion?: string;
  /** Where "open the meeting" goes, when the meeting title is shown. */
  meetingHref?: (meetingId: string) => string;
  /** Shown once loaded when there is no report to show (reports switched
   *  off, a note without lines, no finished meeting). Default: nothing. */
  fallback?: ReactNode;
};

export function NoteMeetingReports({
  noteId,
  meetingId,
  noteTitle,
  fallbackRecordingUrl,
  noteVersion,
  meetingHref,
  fallback = null,
}: Props) {
  const reports = useNoteMeetingReports(noteId);
  const generate = useGenerateNoteMeetingReport();
  const [working, setWorking] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Meetings this view already asked the server to build, so a failure is not
  // retried in a loop (phone rule).
  const autoTriedRef = useRef<Set<string>>(new Set());

  // A changed note: read the list again so `stale` is fresh.
  const refetch = reports.refetch;
  const versionRef = useRef(noteVersion);
  useEffect(() => {
    if (versionRef.current === noteVersion) return;
    versionRef.current = noteVersion;
    void refetch();
  }, [noteVersion, refetch]);

  const data = reports.data;
  const entries: NoteMeetingReportEntry[] = (data?.meetings ?? []).filter(
    (m) => !meetingId || m.meetingId === meetingId,
  );
  const pointCount = data?.pointCount ?? 0;
  const anyWorking = Object.values(working).some(Boolean);

  const build = async (id: string, force: boolean) => {
    setWorking((w) => ({ ...w, [id]: true }));
    setErrors((e) => {
      const next = { ...e };
      delete next[id];
      return next;
    });
    try {
      await generate.mutateAsync({ noteId, meetingId: id, force });
    } catch (err) {
      setErrors((e) => ({
        ...e,
        [id]:
          err instanceof ApiClientError
            ? noteReportErrorText(err.message, err.code)
            : "Could not prepare the report.",
      }));
    } finally {
      setWorking((w) => ({ ...w, [id]: false }));
    }
  };

  // First visit after a link: build the missing reports, one at a time.
  const nextToBuild = entries.find(
    (m) =>
      m.meetingReady &&
      (m.status === "none" || m.status === "pending") &&
      !autoTriedRef.current.has(m.meetingId),
  );
  const nextId = pointCount > 0 && !anyWorking ? nextToBuild?.meetingId : undefined;
  useEffect(() => {
    if (!nextId) return;
    autoTriedRef.current.add(nextId);
    void build(nextId, false);
    // `build` is recreated each render; the id is what decides a run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextId]);

  if (reports.isLoading) return null;
  if (!data || pointCount === 0) return <>{fallback}</>;
  const shown = entries.filter((m) => m.meetingReady || m.status === "ready");
  if (shown.length === 0) return <>{fallback}</>;

  return (
    <>
      {shown.map((m) => {
        const busy = !!working[m.meetingId];
        const err =
          errors[m.meetingId] ||
          (m.status === "failed" && !busy ? noteReportErrorText(m.error) : null);
        // Nothing said is a fact, not a failure: muted, no retry (03806ae).
        const nothingSaid = err === NOTHING_SAID_MESSAGE;
        const report = m.report;
        const url = m.recordingUrl ?? fallbackRecordingUrl ?? null;
        return (
          <section key={m.meetingId} className="mb-8">
            {noteTitle !== undefined ? (
              <h4 className="mb-2 flex items-center gap-2 text-[17px] font-semibold text-app-text">
                {/* DES 08-10-2026: app icon, was NotebookPen */}
                <PenLine className="h-5 w-5 text-brand" aria-hidden />
                <span className="min-w-0 truncate">{noteTitle.trim() || "Untitled note"}</span>
              </h4>
            ) : (
              <>
                <h4 className="mb-1 flex items-center gap-2 text-[17px] font-semibold text-app-text">
                  {/* DES 08-10-2026: app icon, was Presentation */}
                  <BoardroomTable className="h-5 w-5 text-brand" aria-hidden />
                  From the meeting
                </h4>
                {meetingHref ? (
                  <Link
                    to={meetingHref(m.meetingId)}
                    className="mb-2 block truncate text-[15px] font-semibold text-brand hover:underline"
                  >
                    {m.title || "Untitled meeting"}
                  </Link>
                ) : (
                  <p className="mb-2 truncate text-[15px] font-semibold text-brand">
                    {m.title || "Untitled meeting"}
                  </p>
                )}
              </>
            )}

            {busy ? (
              <p className="flex items-center gap-2 py-2 text-[15px] text-app-text-secondary">
                <LoaderCircle className="h-4 w-4 animate-spin text-brand" />
                Reading the meeting for your points…
              </p>
            ) : null}

            {!busy && err ? (
              <p className="flex flex-wrap items-center gap-2 py-2 text-[15px] text-app-text-secondary">
                <span>{err}</span>
                {nothingSaid ? null : (
                  <button
                    type="button"
                    onClick={() => void build(m.meetingId, false)}
                    className="font-semibold text-brand hover:underline"
                  >
                    Try again
                  </button>
                )}
              </p>
            ) : null}

            {!busy && !err && !report && !m.meetingReady ? (
              <p className="text-sm text-app-text-secondary">
                Ready once the meeting has finished processing.
              </p>
            ) : null}

            {report && !busy ? (
              <>
                <p className="mb-4 text-sm font-medium text-app-text-secondary">
                  {report.counts.discussed + report.counts.partly} of {report.counts.total} point
                  {report.counts.total === 1 ? "" : "s"} came up
                  {report.counts.partly ? ` (${report.counts.partly} partly)` : ""}
                </p>

                {m.stale ? (
                  <div className="mb-4 flex flex-wrap items-center gap-3">
                    <span className="text-sm text-app-text-secondary">
                      Your note or the meeting changed since this was made.
                    </span>
                    <button
                      type="button"
                      onClick={() => void build(m.meetingId, false)}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:underline"
                    >
                      <RefreshCw className="h-3.5 w-3.5" />
                      Update
                    </button>
                  </div>
                ) : null}

                <ul className="space-y-5">
                  {report.points.map((p) => (
                    <li key={`${m.meetingId}:p${p.i}`} className="flex gap-2.5">
                      <span className="w-6 shrink-0 pt-0.5">
                        <StatusIcon status={p.status} />
                      </span>
                      <div className="flex min-w-0 flex-1 flex-col gap-1">
                        <p
                          dir={isRTLText(p.point) ? "rtl" : "ltr"}
                          className={cn(
                            "text-base font-semibold leading-[22px]",
                            p.status === "not_discussed" ? "text-app-text-secondary" : "text-app-text",
                          )}
                        >
                          {p.point}
                        </p>
                        <p className="text-[13px] font-medium text-app-text-secondary">
                          {STATUS_LABEL[p.status]}
                          {p.isQuestion && p.answered ? ` · ${ANSWERED_LABEL[p.answered]}` : ""}
                        </p>
                        {p.summary ? (
                          <p
                            dir={isRTLText(p.summary) ? "rtl" : "ltr"}
                            className="text-[15px] leading-[22px] text-app-text-secondary"
                          >
                            {p.summary}
                          </p>
                        ) : null}
                        {p.isQuestion && p.answer ? (
                          <p
                            dir={isRTLText(p.answer) ? "rtl" : "ltr"}
                            className="text-[15px] leading-[22px] text-app-text-secondary"
                          >
                            <span className="font-semibold">Answer: </span>
                            {p.answer}
                          </p>
                        ) : null}
                        {p.anchor?.quote ? (
                          <p
                            dir={isRTLText(p.anchor.quote) ? "rtl" : "ltr"}
                            className="line-clamp-3 text-sm leading-[21px] text-app-text-secondary"
                          >
                            {`“${shortQuote(p.anchor.quote)}”`}
                          </p>
                        ) : null}
                        <MomentButton url={url} anchor={p.anchor} />
                      </div>
                    </li>
                  ))}
                </ul>

                {report.myTodos.length > 0 ? (
                  <div className="mt-8 space-y-4">
                    <p className="flex items-center gap-2 text-base font-semibold text-app-text">
                      <TodoTick className="h-[18px] w-[18px] text-brand" aria-hidden />
                      Your to-dos from this meeting
                    </p>
                    <ul className="space-y-4">
                      {report.myTodos.map((t, idx) => (
                        <li key={`${m.meetingId}:t${idx}`} className="flex gap-2.5">
                          <span className="w-6 shrink-0 pt-0.5">
                            {t.done ? (
                              <CircleCheck className="h-5 w-5 text-brand" />
                            ) : (
                              <Circle className="h-5 w-5 text-app-text-secondary/60" />
                            )}
                          </span>
                          <div className="flex min-w-0 flex-1 flex-col gap-1">
                            <p
                              dir={isRTLText(t.task) ? "rtl" : "ltr"}
                              className={cn(
                                "text-[15px] leading-[22px]",
                                t.done ? "text-app-text-secondary line-through" : "text-app-text",
                              )}
                            >
                              {t.task}
                            </p>
                            {t.team || t.dueDate ? (
                              <p className="text-[13px] font-medium text-app-text-secondary">
                                {[t.team ? "Team" : null, t.dueDate].filter(Boolean).join(" · ")}
                              </p>
                            ) : null}
                            <MomentButton url={url} anchor={t.anchor} />
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                <p className="mt-5 flex items-center gap-1.5 text-xs text-app-text-secondary">
                  <Lock className="h-3 w-3" aria-hidden />
                  Only you can see this. The meeting&apos;s own notes are not changed.
                </p>
              </>
            ) : null}
          </section>
        );
      })}
    </>
  );
}
